import { useState, type FormEvent } from 'react';

import { Button, Group, TextInput } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

export interface AddProfileFormProps {
  subjectId: string;
  /** Inert while any mutation is in flight. */
  disabled?: boolean;
  /** Resolves when the row is added; rejects with the hook's error (already
   * toasted), in which case the fields keep their values for a retry. */
  onAdd: (subjectId: string, url: string, note?: string | null) => Promise<unknown>;
}

/**
 * Lets the reviewer add a profile the system missed. The row arrives
 * already confirmed, so no verdict is asked for; the URL is validated by
 * the backend (a screened platform, not a duplicate) and its message is
 * toasted by the hook.
 */
export function AddProfileForm({
  subjectId,
  disabled = false,
  onAdd,
}: Readonly<AddProfileFormProps>) {
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const trimmed = url.trim();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!trimmed || disabled) {
      return;
    }
    try {
      await onAdd(subjectId, trimmed, note.trim() || null);
      setUrl('');
      setNote('');
    } catch {
      // toasted by the hook
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} data-testid={`add-profile-form-${subjectId}`}>
      <Group gap="xs" wrap="nowrap" align="flex-end">
        <TextInput
          size="xs"
          placeholder="https://…"
          aria-label="Profile URL"
          value={url}
          onChange={(e) => setUrl(e.currentTarget.value)}
          disabled={disabled}
          style={{ flex: 2, minWidth: 0 }}
        />
        <TextInput
          size="xs"
          placeholder="Note (optional)"
          aria-label="Profile note"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          disabled={disabled}
          style={{ flex: 1, minWidth: 0 }}
        />
        <Button
          type="submit"
          size="xs"
          variant="default"
          leftSection={<IconPlus size={14} />}
          disabled={disabled || !trimmed}
          data-testid="add-profile"
        >
          Add profile
        </Button>
      </Group>
    </form>
  );
}
